// Page de connexion moderne et professionnelle avec logo
// Fichier: /var/www/squarecom/frontend/src/pages/Login.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Divider,
  Link,
  useTheme,
  alpha
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Apartment,
  Security,
  Speed,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Force reload to ensure App.js reads the new token
      window.location.href = '/dashboard';

    } catch (err) {
      setError('Email ou mot de passe incorrect');
      setLoading(false);
    }
  };

  const features = [
    { icon: <Speed />, title: 'Gestion rapide', desc: 'Interventions en temps réel' },
    { icon: <Security />, title: 'Sécurisé', desc: 'Données protégées' },
    { icon: <CheckCircle />, title: 'Efficace', desc: 'Processus optimisés' }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: `linear-gradient(135deg,
          ${alpha(theme.palette.primary.main, 0.1)} 0%,
          ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: alpha(theme.palette.primary.main, 0.1),
          top: -200,
          right: -200,
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: alpha(theme.palette.primary.main, 0.08),
          bottom: -150,
          left: -150,
          zIndex: 0
        }}
      />

      <Container component="main" maxWidth="lg" sx={{ zIndex: 1, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: '100vh',
            gap: 6
          }}
        >
          {/* Left side - Branding */}
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Box sx={{ mb: 3 }}>
                <img 
                  src="/img/squarehabitat.svg" 
                  alt="Square Habitat" 
                  style={{ 
                    height: 80,
                    width: 'auto',
                    marginBottom: 16
                  }}
                />
              </Box>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                Gestion intelligente des résidences
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                Simplifiez la gestion de vos propriétés avec notre plateforme moderne.
                Interventions, QR codes, gestion des locataires - tout en un seul endroit.
              </Typography>
            </Box>

            {/* Features */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    flex: '1 1 200px'
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main'
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right side - Login form */}
          <Box
            sx={{
              flex: { xs: 1, md: 0 },
              minWidth: { md: 400 },
              maxWidth: { xs: '100%', md: 440 }
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                background: 'white',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src="/img/squarehabitat.svg" 
                    alt="Square Habitat" 
                    style={{ 
                      height: 56,
                      width: 'auto'
                    }}
                  />
                </Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Connexion
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accédez à votre espace de gestion
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Adresse email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />

                {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: loading ? 'none' : '0 4px 12px rgba(33, 150, 243, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
                    }
                  }}
                >
                  {loading ? 'Connexion en cours...' : 'Se connecter'}
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Link
                    href="#"
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Mot de passe oublié ?
                  </Link>
                </Box>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    OU
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Première visite ?
                    <Link
                      href="/scan"
                      sx={{
                        ml: 1,
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Scanner un QR Code
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                © 2024 Square Com. Tous droits réservés.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;
